/**
 * stagePipelineAdmin.service.ts — Admin > Stage Pipeline (legacy
 * lib/admin/admin-stage-rules.php and admin-stage-details.php): the account's
 * stages in pipeline order and, per pipeline, what a plan in one stage may move
 * to, which arrows it shows and which stages it hides, through /stage-pipeline.
 * Writes need Admin Access; reads stay open.
 *
 *   GET    /stage-pipeline?pipelineTypeId=          page state
 *   GET    /stage-pipeline/rules?fromStageId=&pipelineTypeId=
 *   POST   /stage-pipeline/stages                   { name, typeId }
 *   POST   /stage-pipeline/stages/reorder           { ids } (live stages after Inquiry)
 *   PUT    /stage-pipeline/stages/{id}              { name?, typeId? }
 *   POST   /stage-pipeline/stages/{id}/restore
 *   GET    /stage-pipeline/stages/{id}/automations  tables, blockers, warnings
 *   DELETE /stage-pipeline/stages/{id}              { targetStageId } → movedPlans;
 *                                                   422 STAGE_IN_USE while blocked
 *   PUT    /stage-pipeline/rules                    { fromStageId, toStageId,
 *                                                   pipelineTypeId, moveTo, showArrow }
 *   PUT    /stage-pipeline/rules/hidden             { fromStageId, pipelineTypeId, hiddenIds }
 *   POST   /stage-pipeline/pipelines                { title }
 *   PUT    /stage-pipeline/pipelines/{id}           { title } (Default is not editable)
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

/** The Default pipeline (not a row; its rules carry pipeline 0). */
export const DEFAULT_PIPELINE_ID = 0;
/** A stage's global type: 0 = Custom, 1 = Inquiry (always first, keeps its type). */
export const CUSTOM_STAGE_TYPE = 0;
export const INQUIRY_STAGE_TYPE = 1;
export const STAGE_NAME_MAX = 25;
export const PIPELINE_TITLE_MAX = 25;

export interface PipelineStage {
  /** The custom stage id (accountCustomStageID). */
  id: number;
  name: string;
  /** Its global stage type: 0 = Custom, 1 = Inquiry. */
  typeId: number;
  typeName: string;
  order: number;
  /** false = deleted (restorable). */
  active: boolean;
  isInquiry: boolean;
}

/** A pipeline; id 0 is Default. */
export interface Pipeline {
  id: number;
  name: string;
}

/** A live plan type on the selected pipeline. */
export interface PipelinePlanType {
  id: number;
  name: string;
}

/** A global stage type no stage of the account uses yet (Custom and Inquiry are not listed). */
export interface StageTypeOption {
  id: number;
  name: string;
}

export interface StagePipelineState {
  /** Live then deleted, Inquiry first, then by order. */
  stages: PipelineStage[];
  /** Default first, then the live pipelines. */
  pipelines: Pipeline[];
  pipelineTypeId: number;
  planTypes: PipelinePlanType[];
  stageTypes: StageTypeOption[];
}

/** One live to-stage for a from-stage; a missing rule reads as no move, arrow, shown. */
export interface StageRule {
  toStageId: number;
  moveTo: boolean;
  showArrow: boolean;
  hidden: boolean;
}

export interface StageRules {
  fromStageId: number;
  pipelineTypeId: number;
  rules: StageRule[];
}

export interface StageRuleInput {
  fromStageId: number;
  toStageId: number;
  pipelineTypeId: number;
  moveTo: boolean;
  showArrow: boolean;
}

/** A saved rule; its hidden flag is kept from before. */
export interface SavedStageRule extends StageRuleInput {
  hidden: boolean;
}

/** An Add Tasks / Remove Tasks row: a task item of an active group the stage starts or stops. */
export interface StageTaskRow {
  taskId: number;
  taskName: string;
  taskGroupId: number;
  taskGroupName: string;
  timeType: string;
  timeAmount: number;
  dateType: number;
  customDate: string;
  recurring: number;
  recurringType: string;
  recurringAmount: number;
  recurringNumber: number;
  /** "3 days after assignment date". */
  start: string;
  /** "Once" or "Recurring 4 times, 1 weeks". */
  recur: string;
}

/** A Triggers row: an appointment stage rule that moves plans into the stage. */
export interface StageTriggerRow {
  id: number;
  standardType: string;
  appointmentTypeId: number;
  appointmentTypeGroupId: number | null;
  appointmentTypeGroupName: string | null;
  apptAction: string;
  stageId: number;
  /** "When a consultation appointment is booked change plan stage to Consultation". */
  text: string;
}

export type StageBlockerKind =
  | 'task'
  | 'taskGroupItem'
  | 'taskGroupStageTrigger'
  | 'taskGroupStatusTrigger'
  | 'taskGroupStart'
  | 'taskGroupStop'
  | 'appointmentTypeGroup'
  | 'appointmentStageRule';

/** A live automation that names the stage or one of its statuses; it blocks the delete. */
export interface StageBlocker {
  kind: StageBlockerKind;
  id: number;
  name: string;
}

/** An appointment setting that sends plans to the stage's type; it never blocks. */
export interface StageWarning {
  setting: string;
  label: string;
}

export interface StageAutomations {
  stageId: number;
  addTasks: StageTaskRow[];
  removeTasks: StageTaskRow[];
  triggers: StageTriggerRow[];
  blockers: StageBlocker[];
  /** Active, pending plan tasks that would move a plan to the stage or a status. */
  pendingPlanTasks: number;
  warnings: StageWarning[];
}

/** The delete was refused: automations or pending plan tasks still point at the stage. */
export class StageInUseError extends Error {
  readonly code = 'STAGE_IN_USE';

  constructor(
    message: string,
    public readonly blockers: StageBlocker[],
    public readonly pendingPlanTasks: number,
  ) {
    super(message);
    this.name = 'StageInUseError';
  }
}

// ─── Reads ──────────────────────────────────────────────────────────────────

export function getStagePipeline(pipelineTypeId: number = DEFAULT_PIPELINE_ID): Promise<StagePipelineState> {
  return apiClient
    .get<ApiResponse<Partial<StagePipelineState> | null>>('/stage-pipeline', { params: { pipelineTypeId } })
    .then((r) => {
      const d = r.data.data;
      return {
        stages: d?.stages ?? [],
        pipelines: d?.pipelines ?? [],
        pipelineTypeId: d?.pipelineTypeId ?? pipelineTypeId,
        planTypes: d?.planTypes ?? [],
        stageTypes: d?.stageTypes ?? [],
      };
    });
}

export function getStageRules(fromStageId: number, pipelineTypeId: number): Promise<StageRules> {
  return apiClient
    .get<ApiResponse<Partial<StageRules> | null>>('/stage-pipeline/rules', { params: { fromStageId, pipelineTypeId } })
    .then((r) => toRules(r.data.data, fromStageId, pipelineTypeId));
}

export function getStageAutomations(id: number): Promise<StageAutomations> {
  return apiClient
    .get<ApiResponse<Partial<StageAutomations> | null>>(`/stage-pipeline/stages/${id}/automations`)
    .then((r) => {
      const d = r.data.data;
      return {
        stageId: d?.stageId ?? id,
        addTasks: d?.addTasks ?? [],
        removeTasks: d?.removeTasks ?? [],
        triggers: d?.triggers ?? [],
        blockers: d?.blockers ?? [],
        pendingPlanTasks: Number(d?.pendingPlanTasks) || 0,
        warnings: d?.warnings ?? [],
      };
    });
}

function toRules(d: Partial<StageRules> | null | undefined, fromStageId: number, pipelineTypeId: number): StageRules {
  return {
    fromStageId: d?.fromStageId ?? fromStageId,
    pipelineTypeId: d?.pipelineTypeId ?? pipelineTypeId,
    rules: d?.rules ?? [],
  };
}

// ─── Stage writes ───────────────────────────────────────────────────────────

/** Added last in the pipeline, with starting rules in every pipeline. */
export function createStage(name: string, typeId: number = CUSTOM_STAGE_TYPE): Promise<PipelineStage> {
  return apiClient
    .post<ApiResponse<PipelineStage>>('/stage-pipeline/stages', { name: name.trim(), typeId })
    .then((r) => r.data.data);
}

/** Sends only the fields given. */
export function updateStage(id: number, changes: { name?: string; typeId?: number }): Promise<PipelineStage> {
  const body: { name?: string; typeId?: number } = {};
  if (changes.name !== undefined) body.name = changes.name.trim();
  if (changes.typeId !== undefined) body.typeId = changes.typeId;
  return apiClient.put<ApiResponse<PipelineStage>>(`/stage-pipeline/stages/${id}`, body).then((r) => r.data.data);
}

/** The live stages after Inquiry, in their new order. */
export function reorderStages(ids: number[]): Promise<void> {
  return apiClient.post('/stage-pipeline/stages/reorder', { ids }).then(() => undefined);
}

/** Live again, last in the pipeline, with its rules as before. */
export function restoreStage(id: number): Promise<PipelineStage> {
  return apiClient.post<ApiResponse<PipelineStage>>(`/stage-pipeline/stages/${id}/restore`).then((r) => r.data.data);
}

/**
 * Moves every plan in the stage to the target stage, quietly (no task, text or
 * email), then deactivates the stage. Resolves with the number of plans moved.
 * Throws StageInUseError while automations or pending plan tasks point at the
 * stage; any other failure is rethrown as is.
 */
export async function deleteStage(id: number, targetStageId: number): Promise<number> {
  try {
    const r = await apiClient.delete<ApiResponse<{ deleted: boolean; movedPlans: number }>>(
      `/stage-pipeline/stages/${id}`,
      { data: { targetStageId } },
    );
    return Number(r.data.data?.movedPlans) || 0;
  } catch (err) {
    const res = (err as {
      response?: {
        status?: number;
        data?: { error?: { code?: string; message?: string; details?: { blockers?: StageBlocker[]; pendingPlanTasks?: number } } };
      };
    })?.response;
    const error = res?.data?.error;
    if (res?.status === 422 && error?.code === 'STAGE_IN_USE') {
      throw new StageInUseError(
        error.message || 'Automations still point at this stage',
        Array.isArray(error.details?.blockers) ? error.details.blockers : [],
        Number(error.details?.pendingPlanTasks) || 0,
      );
    }
    throw err;
  }
}

// ─── Rule writes ────────────────────────────────────────────────────────────

/** Move-to and arrow for one (from, to, pipeline); the hidden flag is kept. */
export function saveStageRule(rule: StageRuleInput): Promise<SavedStageRule> {
  const { fromStageId, toStageId, pipelineTypeId, moveTo, showArrow } = rule;
  return apiClient
    .put<ApiResponse<SavedStageRule>>('/stage-pipeline/rules', { fromStageId, toStageId, pipelineTypeId, moveTo, showArrow })
    .then((r) => r.data.data);
}

/** The listed live stages are hidden for the from-stage, the rest shown. */
export function saveHiddenStages(fromStageId: number, pipelineTypeId: number, hiddenIds: number[]): Promise<StageRules> {
  return apiClient
    .put<ApiResponse<Partial<StageRules> | null>>('/stage-pipeline/rules/hidden', { fromStageId, pipelineTypeId, hiddenIds })
    .then((r) => toRules(r.data.data, fromStageId, pipelineTypeId));
}

// ─── Pipeline writes ────────────────────────────────────────────────────────

/** Starts with a copy of Default's rules. */
export function createPipeline(title: string): Promise<Pipeline> {
  return apiClient.post<ApiResponse<Pipeline>>('/stage-pipeline/pipelines', { title: title.trim() }).then((r) => r.data.data);
}

export function renamePipeline(id: number, title: string): Promise<Pipeline> {
  return apiClient.put<ApiResponse<Pipeline>>(`/stage-pipeline/pipelines/${id}`, { title: title.trim() }).then((r) => r.data.data);
}
